/**
 * Paginagebruik meten (besluit Joris, 26 sep 2026) — een licht eigen logje, geen derde partij,
 * geen cookies. Bij elke routewissel gaat één beacon naar de engine met:
 *   - route: het route-SJABLOON (bv. /projects/:id) — nooit een echt id, e-mailadres of
 *     querystring;
 *   - vorige_route: het sjabloon van de pagina daarvoor;
 *   - duur_ms: hoe lang je op die vorige pagina zat (afgerond);
 *   - omgeving: productie | test | lokaal.
 * Wie je bent weet de server uit je inlogtoken; hij bewaart alleen een per dag wisselende
 * hash (unieke gebruikers per dag tellen, niet over dagen volgen).
 */

import { matchPath } from "react-router-dom";
import ROUTES from "../config/routeSjablonen.json";

// ALLOWLIST (Codex-review 26 sep): alleen bekende route-sjablonen uit App.js komen in het
// logje. Een heuristiek ("lijkt dit segment op een id?") liet vrije tekst door
// (/planning/corvee/joris-jansen). Bron = config/routeSjablonen.json; de test
// `paginaGebruik.test.js` houdt die gelijk aan de <Route path>'s in App.js en aan de
// engine-kopie (ai-engine/services/route_sjablonen.json), waartegen de server valideert.
export const ROUTE_SJABLONEN = ROUTES.sjablonen;
export const ONBEKEND = "(onbekend)";

// Eerst exacte (statische) routes, dan met parameter, dan splat (/planning/corvee/*)
const _statisch = new Set(ROUTE_SJABLONEN.filter((s) => !s.includes(":") && !s.includes("*")));
const _dynamisch = [
  ...ROUTE_SJABLONEN.filter((s) => s.includes(":") && !s.includes("*")),
  ...ROUTE_SJABLONEN.filter((s) => s.includes("*")),
];

/**
 * Pad → route-sjabloon uit de allowlist. Querystring en hash gaan er altijd af; een pad
 * dat op geen enkel bekend sjabloon past wordt "(onbekend)" — nooit vrije tekst.
 */
export function routeSjabloon(pad) {
  let p = String(pad || "/").split(/[?#]/)[0] || "/";
  if (p.length > 1) p = p.replace(/\/+$/, "");
  if (_statisch.has(p)) return p;
  const hit = _dynamisch.find((s) => matchPath({ path: s, end: true, caseSensitive: true }, p));
  return hit || ONBEKEND;
}

export function huidigeOmgeving(hostname = window.location.hostname) {
  if (hostname === "localhost" || hostname === "127.0.0.1") return "lokaal";
  if (hostname === "test.fullframe.nl") return "test";
  return "productie";
}

/**
 * De meter zelf, zonder React of netwerk (testbaar). `bezoek(pad, t)` meldt een
 * routewissel; dezelfde route twee keer achter elkaar (re-render, alleen de querystring
 * wijzigt, StrictMode) telt één keer.
 */
export function maakMeter({ verstuur, omgeving = huidigeOmgeving(), nu = () => Date.now() }) {
  let huidig = null; // { route, sinds }
  return {
    bezoek(pad, t = nu()) {
      const route = routeSjabloon(pad);
      if (huidig && huidig.route === route) return null;
      const event = {
        route,
        vorige_route: huidig ? huidig.route : null,
        duur_ms: huidig ? Math.max(0, Math.round(t - huidig.sinds)) : null,
        omgeving,
      };
      huidig = { route, sinds: t };
      try { verstuur(event); } catch { /* meten mag nooit de app hinderen */ }
      return event;
    },
  };
}

/** Wachttijd na een routewissel: een directe redirect (/ → /vandaag) telt zo als één bezoek. */
export const DEBOUNCE_MS = 400;
